import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { Conversation } from "@/api/models";
import { getConversations } from "@/api/api";
import { doTrackEvent } from "@/utils/utils";
import { dedupeConversationsById } from "@/utils/conversations";
import { ChatHistoryListItemCell } from "./ChatHistoryListItem";

/** Number of conversations returned per page by /api/conversations/load (see backend). */
const PAGE_SIZE = 25;

const containerVariants = {
  collapsed: { height: 0, opacity: 0, transition: { when: "afterChildren" } },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      when: "beforeChildren",
    },
  },
};

const itemVariants = {
  collapsed: { opacity: 0, y: -10 },
  expanded: { opacity: 1, y: 0 },
};

const ChatHistoryList = () => {
  const appStateContext = useContext(AppStateContext);
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Infinite-scroll paging: the initial page (offset 0) is loaded by AppProvider,
  // so we start requesting from PAGE_SIZE and append each subsequent page.
  const [offset, setOffset] = useState<number>(PAGE_SIZE);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const isFetchingRef = useRef<boolean>(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Always read the freshest history so appended pages don't overwrite newer state.
  const chatHistoryRef = useRef<Conversation[] | null>(appStateContext?.state.chatHistory ?? null);
  chatHistoryRef.current = appStateContext?.state.chatHistory ?? null;

  /** 
   * Handle setting initial expanded state based on localStorage
  **/
  useEffect(() => {
    if (localStorage.getItem("sms-gpt_chat-expanded") == null || localStorage.getItem("sms-gpt_chat-expanded") === "1") setExpanded(true);
  }, []);

  /** 
   * Handle changing loading state when chat history is loaded
  **/
  useEffect(() => {
    if (appStateContext?.state.chatHistory !== null) {
      setChatHistory(appStateContext?.state.chatHistory.sort((a, b) => (b.pinned || 0) - (a.pinned || 0)).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) || []);
      setLoading(false);
    }
  }, [appStateContext?.state.chatHistory]);

  /**
   * Fetch the next page of conversations and append it to the global chat history.
  **/
  const handleFetchHistory = async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setShowSpinner(true);

    const response = await getConversations(offset);
    if (response) {
      if (response.length < PAGE_SIZE) setHasMore(false);
      if (response.length > 0) {
        setOffset((prev) => prev + PAGE_SIZE);
        const current = chatHistoryRef.current;
        // Dedupe: a page can contain a recently-updated pinned chat that is already loaded.
        appStateContext?.dispatch({ type: 'LOAD_CHAT_HISTORY', payload: current ? dedupeConversationsById(current.concat(response)) : response });
      }
    }

    setShowSpinner(false);
    isFetchingRef.current = false;
  };

  // Keep the latest fetch handler in a ref so the (stable) sentinel callback ref
  // always invokes the current closure — with up-to-date offset/hasMore — without rebinding.
  const fetchMoreRef = useRef<() => void>(() => {});
  fetchMoreRef.current = handleFetchHistory;

  /**
   * Callback ref for the sentinel: (re)attaches the IntersectionObserver the moment the
   * sentinel mounts, and disconnects when it unmounts. This avoids the timing gap where a
   * plain effect runs before the list (and sentinel) exist during the initial loading state.
  **/
  const setSentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchMoreRef.current();
      },
      { rootMargin: "200px" }
    );
    observerRef.current.observe(node);
  }, []);

  /**
   * Handle toggling the expanded state of the list
  **/
  const toggleExpanded = () => {
    const newValue = !expanded;
    setExpanded(newValue);
    localStorage.setItem("sms-gpt_chat-expanded", newValue ? "1" : "0");
  };
  
  if (!chatHistory || chatHistory.length === 0) return <div className="tour-step-9"></div>;

  return (
    <div className="text-[13px] px-1">
      <button type="button" className="btn btn-xs btn-ghost justify-start items-center w-full text-black/50 dark:text-white/50 text-xs p-0 mt-4 tour-step-9" onClick={() => { toggleExpanded(); handleTrackEvent('click', 'conv_toggle', 'Conversation Toggle', '', '', ''); }}>
        Chats
        {expanded ? <ChevronDown size={14} className="mt-0.5" /> : <ChevronRight size={14} className="mt-0.5" /> }
      </button>

      <motion.div
        initial="collapsed"
        animate={expanded ? "expanded" : "collapsed"}
        variants={containerVariants}
        style={{ overflow: "hidden" }}
      >
        <AnimatePresence>
          {chatHistory.map((chat, idx) =>(
            <motion.div
              key={chat.id ?? idx}
              variants={itemVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
            >
              <ChatHistoryListItemCell key={idx} item={chat} />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Sentinel: loads the next page when scrolled into view */}
        {hasMore && <div ref={setSentinelRef} className="h-px" />}
        {showSpinner && <div className="text-center mt-3"><span className="loading loading-ring loading-lg"></span></div>}

        <span className="text-[11px] text-accent my-2">Conversations are automatically deleted after 3 months of inactivity</span>
      </motion.div>
    </div>
  );
}

export default ChatHistoryList;